'use client';

import { Fragment, useEffect, useRef } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Lock } from 'lucide-react';
import {
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_MUTED_INK_PLACEHOLDER,
  ATTRIBUTE_VALUE_TEXT,
  attributeLabelWidthStyle,
  attributePlaceholder
} from '@/components/attributes';
import {
  ATTRIBUTE_BARE_CONTROL,
  AttributeEditableCell,
  AttributeRow
} from '../attribute-row';
import {
  AttributeMotionRows,
  type AttributeMotionRow
} from '../attribute-motion-rows';
import type { AssetOwnershipType } from '@/lib/services/redemption';
import type { Contact } from '@/lib/services/contacts/types';
import {
  OWNERSHIP_LABELS,
  OWNERSHIP_OPTIONS,
  INSTRUCTION_LABELS,
  INSTRUCTION_ICONS,
  DIRECT_TRANSFER_STATES,
  getAvailableInstructions,
  instructionChoiceClass,
  instructionLabelClass,
  type PropertyInstructionSectionProps,
  type TransferBeneficiary
} from './constants';
import {
  TransferSubFlowSection,
  BeneficiarySelection,
  ContraryIntentionControl
} from './edit-sub-components';
import { InstructionChoiceMark } from '../instruction-choice-mark';
import { useOptimisticValue } from '../use-optimistic-value';
import { INSTRUCTION_CHOICE_SIZE } from '../instruction-chrome';

type InstructionEditSectionProps = Omit<
  PropertyInstructionSectionProps,
  'mode' | 'milestoneSummary'
>;

export function InstructionEditSection({
  ownershipType,
  instruction,
  transferSubFlow,
  transferBeneficiaries,
  beneficiaryHoldingType,
  hasContraryIntention,
  percentageOwned,
  propertyState,
  hasLinkedMortgage,
  contacts,
  onOwnershipTypeChange,
  onInstructionChange,
  onTransferSubFlowChange,
  onTransferBeneficiariesChange,
  onBeneficiaryHoldingTypeChange,
  onContraryIntentionChange,
  onPercentageOwnedChange,
  variant = 'form'
}: InstructionEditSectionProps) {
  const compact = variant === 'compact';
  /*
    **The ownership type the reader last chose, until the write lands** — every row in
    this block derives from it (which instructions exist, whether a share row shows,
    whether the type is locked to Notice of Death), so masking it here is what makes
    the whole cascade change in one frame instead of arriving a round-trip later, in
    stages. {@link useOptimisticValue} carries the reasoning.
  */
  const ownership = useOptimisticValue<string | null | undefined>(
    ownershipType
  );
  const shownOwnership = ownership.shown;
  const available = getAvailableInstructions(shownOwnership);
  const isJointTenants = shownOwnership === 'joint-tenants';
  const isTransfer = instruction === 'transfer-to-beneficiary';
  const showContraryIntention = isTransfer && !!hasLinkedMortgage;

  /**
   * **Joint tenants can only be a Notice of Death, so set it — once** (crash fixed
   * in VALE-355; the bug predates it and is reachable on `develop`).
   *
   * This was a three-line effect that took down the whole matter page with
   * *"Maximum update depth exceeded"*, after firing **54 successful `PUT`s**.
   * Reproduced by choosing `Joint Tenants` on a `transfer-to-beneficiary` property.
   * Two faults compounded:
   *
   * · **`onInstructionChange` was in the dependency array, and it is a fresh arrow
   *   on every render** — `propertyInstructionBlock` builds it inline, so the effect
   *   re-ran after *every* render rather than on the transition it cares about. Any
   *   render in which `instruction` was not yet `notice-of-death` issued another
   *   write, and each write caused another render.
   * · **Two writes competed for `itemInfo.instruction`.** Selecting the type cleared
   *   the now-invalid instruction to `null` *and* this effect set it to
   *   `notice-of-death`; with both in flight, whichever response landed last decided
   *   the field, so a `null` landing second re-armed the effect. See
   *   {@link handleOwnershipChange}, which no longer sends the competing clear.
   *
   * The callback is read through a ref so the dependency array can be honest about
   * what this effect actually reacts to, and `autoSetForRef` makes it one-shot per
   * entry into joint tenancy — so even a *failing* write can no longer spin, which
   * a condition-only guard could not promise.
   */
  const onInstructionChangeRef = useRef(onInstructionChange);
  /*
    Kept current in an effect rather than assigned during render — a ref is not a
    render output, and the lint rule that says so is guarding a real hazard. This
    effect is declared *before* the one that reads it, and effects run in declaration
    order, so the callback is always fresh by the time the auto-set fires.
  */
  useEffect(() => {
    onInstructionChangeRef.current = onInstructionChange;
  }, [onInstructionChange]);
  const autoSetForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isJointTenants) {
      autoSetForRef.current = null;
      return;
    }
    if (instruction === 'notice-of-death') return;
    if (autoSetForRef.current === 'joint-tenants') return;
    autoSetForRef.current = 'joint-tenants';
    onInstructionChangeRef.current?.('notice-of-death');
  }, [isJointTenants, instruction]);

  /**
   * **One write, and the row shows the new type immediately.**
   *
   * The instruction the new type implies is resolved here and sent *with* the type —
   * see {@link PropertyInstructionSectionProps.onOwnershipTypeChange} for the race
   * that made separate writes silently revert each other. Three outcomes, in the
   * order they are decided:
   *
   * · the current instruction is still valid ⇒ leave it (`undefined`);
   * · the new type allows exactly one ⇒ that one (joint tenants → Notice of Death,
   *   which is also why the effect above almost never has anything to do);
   * · otherwise ⇒ clear it, because the reader has a real choice to make.
   */
  const handleOwnershipChange = (value: string) => {
    const newType = value as AssetOwnershipType;
    const isNonEstate = newType === 'joint-tenants';
    const newAvailable = getAvailableInstructions(newType);
    const nextInstruction =
      instruction && newAvailable.includes(instruction)
        ? undefined
        : newAvailable.length === 1
          ? newAvailable[0]
          : null;
    /*
      **Pre-arm the auto-set guard when this patch already carries the instruction.**

      The effect above exists for a record that *arrives* joint-tenants with an
      instruction it cannot hold. It must not also fire on this transition: the
      instruction is in the patch below, and the effect's write is a *second* `PUT`
      that read-modify-writes `itemInfo` from the pre-change snapshot — which is
      precisely how `ownershipType` got reverted (see the prop's doc). Making the
      shown ownership optimistic is what exposed this: the effect now sees
      `isJointTenants` immediately rather than a round-trip later.
    */
    if (newType === 'joint-tenants') autoSetForRef.current = 'joint-tenants';
    ownership.apply(newType, () =>
      onOwnershipTypeChange?.(newType, isNonEstate, nextInstruction)
    );
  };

  const handleBeneficiaryToggle = (contact: Contact) => {
    const current = transferBeneficiaries ?? [];
    const exists = current.some((b) => b.contactId === contact.id);
    const updated: TransferBeneficiary[] = exists
      ? current.filter((b) => b.contactId !== contact.id)
      : [
          ...current,
          {
            contactId: contact.id,
            contact: {
              id: contact.id,
              firstName: contact.firstName ?? '',
              lastName: contact.lastName ?? ''
            }
          }
        ];
    onTransferBeneficiariesChange?.(updated);

    if (updated.length < 2) {
      onBeneficiaryHoldingTypeChange?.(null);
    }
  };

  const isDirectAvailable = DIRECT_TRANSFER_STATES.includes(
    propertyState ?? ''
  );

  /*
    The two fields that are genuinely label/value become attribute rows in compact
    and keep their stacked form chrome otherwise. Everything below them is a
    multi-way choice, which the treatment has no arm for — those keep their own
    layout and change only ink and type (`instructionChoiceClass`).
  */
  const ownershipSelect = (close?: () => void) => (
    <Select
      value={shownOwnership ?? ''}
      onValueChange={handleOwnershipChange}
      /*
        In compact the row opens *into* this select, so its list should already be
        showing — and closing the list closes the row, because a portalled list
        leaves the cell's own blur check nothing to see.
      */
      defaultOpen={compact || undefined}
      onOpenChange={
        close
          ? (listOpen) => {
              if (!listOpen) close();
            }
          : undefined
      }
    >
      <SelectTrigger
        className={cn(compact ? `${ATTRIBUTE_BARE_CONTROL} w-full` : 'h-9')}
        placeholderColor={compact ? ATTRIBUTE_MUTED_INK_PLACEHOLDER : undefined}
      >
        <SelectValue
          placeholder={
            compact
              ? attributePlaceholder('Ownership type')
              : 'Select ownership type...'
          }
        />
      </SelectTrigger>
      <SelectContent>
        {OWNERSHIP_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  /*
    **In compact this is the same text control every other field on the surface
    uses** (VALE-355). `type="number"` brought the browser's stepper arrows —
    furniture that ignores this surface's type and palette, appears only on hover,
    and is laid out inside the content box where it steals width from the value.
    `inputMode="decimal"` keeps the numeric keypad on touch without any of it, the
    same trade `AttributeMoneyEditor` makes.

    The **form chrome keeps `type="number"`** and its native spinners: those 22
    forms ship that control today and were not reported for it, so changing them
    here would move shipped pixels for a defect they do not have.
  */
  const percentageInput = (
    <Input
      type={compact ? 'text' : 'number'}
      inputMode="decimal"
      {...(compact ? {} : { min: 1, max: 99 })}
      placeholder={
        compact ? attributePlaceholder('Ownership share') : 'e.g. 50'
      }
      className={cn(compact ? `${ATTRIBUTE_BARE_CONTROL} pr-0` : 'h-9')}
      value={percentageOwned ?? ''}
      onChange={(e) => {
        const raw = e.target.value.trim();
        const parsed = raw ? parseInt(raw, 10) : null;
        onPercentageOwnedChange?.(
          parsed !== null && Number.isFinite(parsed) ? parsed : null
        );
      }}
    />
  );

  /*
    Every row of the block, keyed — see the note at the render site for why this is a
    list rather than six conditionals.
  */
  const bodyRows: AttributeMotionRow[] = [
    {
      key: 'ownership-type',
      node: compact ? (
        /*
          Read-out at rest, editor on click — the same motion as the money rows and
          the grid rows around it. Round 6 left this as a permanently open select,
          which was the last box in the block once the balances became read-outs.
        */
        <AttributeRow label="Ownership type">
          <AttributeEditableCell
            display={
              shownOwnership ? (OWNERSHIP_LABELS[shownOwnership] ?? '') : ''
            }
            placeholder={attributePlaceholder('Ownership type')}
            // Portalled list — the select closes the cell, not blur. See
            // `AttributeEditableCell`.
            closeOnBlur={false}
            renderEditor={(close) => ownershipSelect(close)}
          />
        </AttributeRow>
      ) : (
        <div className="space-y-1.5">
          <label className={instructionLabelClass(false)}>Ownership Type</label>
          {ownershipSelect()}
        </div>
      )
    }
  ];

  if (shownOwnership === 'tenants-in-common') {
    bodyRows.push({
      key: 'ownership-share',
      node: compact ? (
        /*
          `Ownership share` rather than `Deceased's Ownership Percentage`: the long
          form needs ~180px against a ~120px label column. The `%` moves out of the
          input and into the cell beside it, the same move the money row's `$` makes —
          chrome in the cell, content in the field.
        */
        <AttributeRow label="Ownership share">
          <AttributeEditableCell
            display={percentageOwned != null ? `${percentageOwned}%` : ''}
            placeholder={attributePlaceholder('Ownership share')}
            renderEditor={() => (
              <>
                {percentageInput}
                <span
                  className={cn(
                    ATTRIBUTE_VALUE_TEXT,
                    ATTRIBUTE_LEADING,
                    ATTRIBUTE_MUTED_INK,
                    'shrink-0 pr-1.5'
                  )}
                  aria-hidden
                >
                  %
                </span>
              </>
            )}
          />
        </AttributeRow>
      ) : (
        <div className="space-y-1.5">
          <label className={instructionLabelClass(false)}>
            Deceased&apos;s Ownership Percentage
          </label>
          <div className="relative w-32">
            {percentageInput}
            <span className="text-foreground-subtle absolute top-1/2 right-3 -translate-y-1/2 text-sm">
              %
            </span>
          </div>
        </div>
      )
    });
  }

  if (!!shownOwnership && available.length > 0) {
    bodyRows.push({
      key: 'instruction',
      node: (
        <div className="space-y-1.5">
          <label className={instructionLabelClass(compact)}>Instruction</label>
          <div className="flex flex-wrap gap-1.5">
            {available.map((inst) => {
              const isActive = instruction === inst;
              const Icon = INSTRUCTION_ICONS[inst];
              const isLocked = isJointTenants;

              return (
                <button
                  key={inst}
                  type="button"
                  disabled={isLocked}
                  onClick={() => onInstructionChange?.(inst)}
                  className={cn(
                    'flex items-center gap-1.5 border transition-colors',
                    compact
                      ? INSTRUCTION_CHOICE_SIZE
                      : 'rounded-full px-3 py-1.5 text-sm',
                    instructionChoiceClass(compact, isActive),
                    isLocked && 'cursor-default opacity-75'
                  )}
                >
                  {isLocked && <Lock className="h-3 w-3" />}
                  <Icon className="h-3.5 w-3.5" />
                  {INSTRUCTION_LABELS[inst]}
                  <InstructionChoiceMark
                    compact={compact}
                    isActive={isActive}
                  />
                </button>
              );
            })}
          </div>
        </div>
      )
    });
  }

  if (isTransfer) {
    bodyRows.push({
      key: 'transfer-method',
      node: (
        <TransferSubFlowSection
          transferSubFlow={transferSubFlow}
          isDirectAvailable={isDirectAvailable}
          propertyState={propertyState}
          onTransferSubFlowChange={onTransferSubFlowChange}
          compact={compact}
        />
      )
    });
  }

  if (isTransfer && !!contacts && contacts.length > 0) {
    bodyRows.push({
      key: 'beneficiaries',
      node: (
        <BeneficiarySelection
          contacts={contacts}
          transferBeneficiaries={transferBeneficiaries}
          beneficiaryHoldingType={beneficiaryHoldingType}
          onBeneficiaryToggle={handleBeneficiaryToggle}
          onBeneficiariesChange={(next) => {
            // Returned, not discarded: the child lifts its optimistic mask when this
            // settles — see its `pending` note.
            const written = onTransferBeneficiariesChange?.(next);
            // Mirrors `handleBeneficiaryToggle`'s own rule: a holding type is only
            // meaningful once two beneficiaries share the property.
            if (next.length < 2) onBeneficiaryHoldingTypeChange?.(null);
            return written;
          }}
          onBeneficiaryHoldingTypeChange={onBeneficiaryHoldingTypeChange}
          onBeneficiaryPercentageChange={(contactId, percentage) => {
            const updated = (transferBeneficiaries ?? []).map((b) =>
              b.contactId === contactId ? { ...b, percentage } : b
            );
            onTransferBeneficiariesChange?.(updated);
          }}
          compact={compact}
        />
      )
    });
  }

  if (showContraryIntention) {
    bodyRows.push({
      key: 'contrary-intention',
      node: (
        <ContraryIntentionControl
          hasContraryIntention={hasContraryIntention}
          onContraryIntentionChange={onContraryIntentionChange}
          compact={compact}
        />
      )
    });
  }

  return (
    <Fragment>
      {/*
        The rule above the block belongs to the *form* chrome. On the attribute
        treatment a section is already ruled by its own group heading, so a second
        hairline here draws a boundary inside one section (§1.10).
      */}
      {!compact && <div className="border-border-subtle w-full border-t" />}
      <div
        className={cn(
          'flex w-full flex-col',
          compact ? '@container gap-2' : 'gap-3'
        )}
        style={compact ? attributeLabelWidthStyle(2) : undefined}
      >
        {/*
          **The block's rows as a keyed list** (round 8).

          Choosing an ownership type or an instruction does not add *one* field here —
          `Tenants in common` adds a share row, and `Transfer to beneficiary` adds a
          transfer method, a beneficiary list and (with a linked mortgage) a
          contrary-intention panel, all in the same click. Rendered as plain
          conditionals every one of them appeared in a single frame and pushed
          `ESTATE` and `NOTES` down with them, which reads as the drawer rebuilding
          rather than as the answer opening a follow-up question.

          The keys are what the motion diffs on, so each is the field's own name and
          stays stable across every render that keeps it. In `form` chrome the same
          list renders as a plain stack — the motion is part of the treatment, and the
          22 shipped forms are not opting into it.
        */}
        {compact ? (
          <AttributeMotionRows className="gap-2" rows={bodyRows} />
        ) : (
          bodyRows.map((row) => <Fragment key={row.key}>{row.node}</Fragment>)
        )}
      </div>
    </Fragment>
  );
}
